"use client"

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Menu, X, Send } from "lucide-react"
import { useState, useEffect, useCallback } from "react"
import { cn } from "@/lib/utils"
import { ModeToggle } from "@/components/mode-toggle"
import { ruItSupportConfig, telegramUrl } from "@/data/ru-it-support/config"

const navItems = [
    { href: "/ru#services", label: "Услуги" },
    { href: "/ru#pricing", label: "Тарифы" },
    { href: "/ru#funding", label: "Субсидии" },
    { href: "/ru#process", label: "Как мы работаем" },
    { href: "/ru#faq", label: "Вопросы" },
    { href: "/ru/blog", label: "Статьи" },
    { href: "/ru#contact", label: "Контакт" },
]

/** Header der russischen Seiten – gleiche Struktur wie Header.tsx, eigene Navigation. */
export function HeaderRu() {
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    const closeMenu = useCallback(() => setIsMenuOpen(false), [])

    useEffect(() => {
        if (!isMenuOpen) return
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeMenu()
        }
        document.addEventListener("keydown", handleEscape)
        return () => document.removeEventListener("keydown", handleEscape)
    }, [isMenuOpen, closeMenu])

    return (
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="bg-primary text-primary-foreground text-xs sm:text-sm py-1.5">
                <div className="container flex items-center justify-center gap-2">
                    <Send className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    <span>Вопрос по IT?</span>
                    <a href={telegramUrl} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">
                        Telegram
                    </a>
                    <span aria-hidden="true">·</span>
                    <a href={ruItSupportConfig.phone.href} className="font-semibold hover:underline">
                        {ruItSupportConfig.phone.display}
                    </a>
                </div>
            </div>
            <div className="container flex h-16 items-center justify-between px-4 md:px-6">
                <Link className="flex items-center gap-2 font-bold text-xl" href="/ru">
                    Webcodez
                </Link>
                <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
                    {navItems.map((item) => (
                        <Link key={item.href} className="transition-colors hover:text-primary" href={item.href}>
                            {item.label}
                        </Link>
                    ))}
                    <Link
                        className="transition-colors hover:text-primary text-muted-foreground"
                        href={ruItSupportConfig.paths.de}
                        hrefLang="de"
                        lang="de"
                        title="Deutsche Version"
                    >
                        DE
                    </Link>
                    <Button asChild size="sm">
                        <Link href="/ru#contact">Бесплатная консультация</Link>
                    </Button>
                    <ModeToggle />
                </nav>
                <div className="flex md:hidden items-center gap-2">
                    <ModeToggle />
                    <Button
                        className="md:hidden"
                        size="icon"
                        variant="ghost"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-expanded={isMenuOpen}
                        aria-controls="mobile-menu"
                        aria-label={isMenuOpen ? "Закрыть меню" : "Открыть меню"}
                    >
                        {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </Button>
                </div>
            </div>
            {/* Mobile Menu */}
            <div
                id="mobile-menu"
                className={cn(
                    "md:hidden border-t bg-background overflow-hidden transition-all duration-200 ease-in-out",
                    isMenuOpen ? "max-h-[36rem] opacity-100" : "max-h-0 opacity-0"
                )}
            >
                <nav className="flex flex-col gap-1 p-4 text-sm font-medium">
                    {navItems.map((item) => (
                        <Link
                            key={item.href}
                            className="transition-colors hover:text-primary py-3 min-h-[44px] flex items-center"
                            href={item.href}
                            onClick={closeMenu}
                        >
                            {item.label}
                        </Link>
                    ))}
                    <Link
                        className="transition-colors hover:text-primary py-3 min-h-[44px] flex items-center text-muted-foreground"
                        href={ruItSupportConfig.paths.de}
                        hrefLang="de"
                        lang="de"
                        onClick={closeMenu}
                    >
                        Deutsche Version
                    </Link>
                    <Button asChild size="default" className="w-full mt-2 min-h-[44px]">
                        <Link href="/ru#contact" onClick={closeMenu}>
                            Бесплатная консультация
                        </Link>
                    </Button>
                </nav>
            </div>
        </header>
    )
}
